import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
  faExchangeAlt
} from "@fortawesome/free-solid-svg-icons";

const CorporateStatusList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statuses, setStatuses] = useState([
    { status_id: 1, status_code: 1, status_name: "Active", status_description: "Corporate is currently active and operational." },
    { status_id: 2, status_code: 2, status_name: "Terminated/Cancelled", status_description: "Corporate contract has been terminated or cancelled." },
    { status_id: 3, status_code: 3, status_name: "Suspended", status_description: "Corporate account is temporarily suspended." },
    { status_id: 4, status_code: 4, status_name: "Expired", status_description: "Corporate contract has expired and is no longer valid." },
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState({ status_code: "", status_name: "", status_description: "" });
  const [editingStatus, setEditingStatus] = useState(null);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(statuses.length / itemsPerPage);
  const paginatedData = statuses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  };

  const handleAddStatus = () => {
    if (newStatus.status_code && newStatus.status_name) {
      if (editingStatus) {
        setStatuses(statuses.map((s) => (s.status_id === editingStatus.status_id ? { ...s, ...newStatus, status_id: s.status_id } : s)));
      } else {
        setStatuses([...statuses, { status_id: statuses.length + 1, ...newStatus }]);
      }
      setNewStatus({ status_code: "", status_name: "", status_description: "" });
      setEditingStatus(null);
      setIsModalOpen(false);
    }
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    setNewStatus({
      status_code: status.status_code.toString(),
      status_name: status.status_name,
      status_description: status.status_description,
    });
    setIsModalOpen(true);
  };

  const handleDeleteStatus = (statusId) => {
    setStatuses(statuses.filter((s) => s.status_id !== statusId));
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-1xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-2xl shadow-md transform transition hover:scale-105">
              <FontAwesomeIcon icon={faExchangeAlt} className="text-indigo-600 text-lg" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                Corporate Status Management
              </h1>
              <p className="text-sm text-gray-500 mt-1">Manage corporate status definitions</p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingStatus(null);
              setNewStatus({ status_code: "", status_name: "", status_description: "" });
              setIsModalOpen(true);
            }}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2 text-xs" />
            Add New Status
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">Status Code</th>
                  <th scope="col" className="px-6 py-3">Status Name</th>
                  <th scope="col" className="px-6 py-3">Description</th>
                  <th scope="col" className="px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr
                      key={item.status_id}
                      className="bg-white border-b hover:bg-gray-50 transition-all duration-200"
                    >
                      <td className="px-6 py-4 font-medium">{item.status_code}</td>
                      <td className="px-6 py-4">{item.status_name}</td>
                      <td className="px-6 py-4">{item.status_description}</td>
                      <td className="px-6 py-4 flex gap-2">
                        <button
                          onClick={() => handleEditStatus(item)}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-all duration-200 text-xs font-medium"
                        >
                          <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStatus(item.status_id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-all duration-200 text-xs font-medium"
                        >
                          <FontAwesomeIcon icon={faTrash} className="mr-1" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                      No statuses found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              Showing{" "}
              {statuses.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}{" "}
              to{" "}
              {Math.min(statuses.length, currentPage * itemsPerPage)} of{" "}
              {statuses.length} entries
            </div>
            <div className="flex items-center">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {renderPaginationNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal for Add/Edit Status */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-96 shadow-xl">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {editingStatus ? "Edit Status" : "Add New Status"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status Code</label>
                  <input
                    type="number"
                    value={newStatus.status_code}
                    onChange={(e) => setNewStatus({ ...newStatus, status_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter status code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status Name</label>
                  <input
                    type="text"
                    value={newStatus.status_name}
                    onChange={(e) => setNewStatus({ ...newStatus, status_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter status name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                  <textarea
                    value={newStatus.status_description}
                    onChange={(e) => setNewStatus({ ...newStatus, status_description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter status description"
                    rows="3"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStatus}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg shadow-md hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium"
                >
                  {editingStatus ? "Save Changes" : "Add Status"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateStatusList;